/**
 * Deal Room home (Files screen 1): one search, and the deals — the unit people
 * navigate by — newest change first.
 */
import { Loader2, Plus, Search, Upload } from "lucide-react";
import React, { useCallback, useMemo, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import ConnectSourceDialog from "../../components/Files/ConnectSourceDialog";
import DealsTable from "../../components/Files/DealsTable";
import { useConnectDataSource } from "../../hooks/useConnectDataSource";
import {
	useDealActivity,
	useDealRoomActions,
} from "../../hooks/useDealRoomActions";
import { filterDeals, useFileLibrary } from "../../hooks/useFileLibrary";
import { useUploadFlow } from "../../hooks/useUploadFlow";
import { splitByShared } from "../../utils/folderSharing";

const FilesHome: React.FC = () => {
	const navigate = useNavigate();
	const library = useFileLibrary();
	const connect = useConnectDataSource();
	const [query, setQuery] = useState("");
	const [connectOpen, setConnectOpen] = useState(false);
	const closeConnect = useCallback(() => setConnectOpen(false), []);
	// Uploading opens the same dialogs as the Data Sources page, in place, and
	// lands on the deal the files went into.
	const uploadFlow = useUploadFlow(({ folderId }) =>
		navigate(`/files/deals/${folderId}`),
	);

	const actions = useDealRoomActions();
	const activityFor = useDealActivity(() => void library.refresh());

	const rows = useMemo(
		() => filterDeals(library.deals, query),
		[library.deals, query],
	);
	// Deals split into the caller's private ones and the shared ones — with
	// the team or with specific people, theirs included. One table when nothing
	// is shared.
	const sections = useMemo(() => {
		const { yours, shared } = splitByShared(rows, (row) => row.deal.node.folder);
		if (shared.length === 0) return null;
		return [
			{ key: "yours", title: "Yours", rows: yours },
			{ key: "shared", title: "Shared", rows: shared },
		].filter((section) => section.rows.length > 0);
	}, [rows]);

	return (
		<div className="flex h-full flex-col bg-white">
			<header className="flex h-16 flex-shrink-0 items-center gap-3 border-b border-[#eef0f2] px-8">
				<h1 className="whitespace-nowrap text-xl font-semibold tracking-tight text-gray-900">
					Deal Room
				</h1>
				<label className="ml-4 flex h-10 max-w-[560px] flex-1 items-center gap-2.5 rounded-[10px] border border-[#d0d5dd] px-3.5 text-text-muted focus-within:border-[#004C4B]">
					<Search className="h-4 w-4 flex-shrink-0" aria-hidden />
					<input
						type="search"
						value={query}
						onChange={(e) => setQuery(e.target.value)}
						aria-label="Search deals and files"
						placeholder="Search deals and files"
						className="w-full border-0 bg-transparent p-0 text-sm text-gray-900 outline-none focus:ring-0"
					/>
				</label>
				<button
					type="button"
					onClick={uploadFlow.start}
					className="ml-auto inline-flex h-9 items-center gap-1.5 whitespace-nowrap rounded-lg border border-gray-200 bg-white px-3.5 text-[13px] font-medium text-gray-800 hover:bg-gray-50"
				>
					<Upload className="h-4 w-4" aria-hidden />
					Upload
				</button>
				{/* Connecting a source is off for now: the button stays in place,
				    greyed, so the way in is visible but not offered. */}
				<button
					type="button"
					disabled
					title="Coming soon"
					onClick={() => setConnectOpen(true)}
					className="inline-flex h-9 cursor-not-allowed items-center gap-1.5 whitespace-nowrap rounded-lg bg-[#ECECEC] px-3.5 text-[13px] font-semibold text-[#9CA3AF]"
				>
					<Plus className="h-4 w-4" aria-hidden />
					Connect a source
				</button>
			</header>

			<div className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto px-8 py-[18px]">
				{library.loading ? (
					<div className="flex items-center gap-2 py-10 text-sm text-text-muted">
						<Loader2 className="h-4 w-4 animate-spin" aria-hidden />
						Loading your deals…
					</div>
				) : library.error ? (
					<p role="alert" className="py-10 text-sm text-[#b54708]">
						{library.error}
					</p>
				) : library.deals.length === 0 ? (
					// No deal yet: the first-run "Let's get started" page is the
					// empty state.
					<Navigate to="/upload" replace />
				) : (
					<>
						<div className="flex items-center text-[12.5px] text-[#5B6472]">
							<span className="font-medium text-gray-900">
								{query.trim()
									? `${rows.length} of ${library.deals.length} deals`
									: `All deals · ${library.deals.length}`}
							</span>
							<span className="ml-auto">Sorted by last change</span>
						</div>
						{rows.length && sections ? (
							sections.map((section) => (
								<section
									key={section.key}
									aria-label={section.title}
									className="flex flex-col gap-2"
								>
									<h2 className="text-[11px] font-semibold uppercase tracking-wider text-text-muted">
										{section.title} · {section.rows.length}
									</h2>
									<DealsTable
										rows={section.rows}
										actions={actions}
										activityFor={activityFor}
									/>
								</section>
							))
						) : rows.length ? (
							<DealsTable
								rows={rows}
								actions={actions}
								activityFor={activityFor}
							/>
						) : (
							<p className="py-6 text-sm text-text-muted">
								No deal or file matches “{query.trim()}”.
							</p>
						)}
					</>
				)}
			</div>

			<ConnectSourceDialog
				isOpen={connectOpen}
				onClose={closeConnect}
				onConnect={(dataSource) => {
					setConnectOpen(false);
					void connect(dataSource);
				}}
				onUpload={() => {
					setConnectOpen(false);
					uploadFlow.start();
				}}
			/>
			{uploadFlow.element}
			{actions.element}
		</div>
	);
};

export default FilesHome;
